'use client';

import { ComboBox } from '~/components/primitives';
import { SUGGESTED_RADIUS_KM } from '@funkey/constants';
import { cx } from 'class-variance-authority';
import { useMemo } from 'react';
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService';

export type GoogleMapsCoords = { lat: number; lng: number };

/** Suggested search radius (km) for the picked place, derived from its Google
 * place `types`. Belgium's admin hierarchy: `administrative_area_level_1` =
 * gewest (Vlaanderen/Wallonië — huge), `administrative_area_level_2` = provincie
 * (Antwerpen, Oost-Vlaanderen…), `locality` = stad/gemeente. A city default of
 * 20 km is far too tight for a whole province, so widen it by granularity. */
function suggestedRadiusForTypes(types: readonly string[] | undefined): number {
    if (!types) return SUGGESTED_RADIUS_KM.city;
    if (types.includes('administrative_area_level_1')) return SUGGESTED_RADIUS_KM.gewest;
    if (types.includes('administrative_area_level_2')) return SUGGESTED_RADIUS_KM.province;
    return SUGGESTED_RADIUS_KM.city; // city / town / precise address
}

export const GoogleMapsAutocomplete: React.FC<
    Pick<
        React.ComponentProps<typeof ComboBox>,
        'value' | 'placeholder' | 'searchPlaceHolder' | 'onChange' | 'compact'
    > & {
        className?: string;
        /** ISO 3166-1 alpha-2 country codes to restrict autocomplete results to. Max 5 per Google Places API. Defaults to Belgium + the Netherlands. */
        countryRestrictions?: string[];
        /** Fires when a place is selected and its coordinates have been resolved. `null` if the lookup failed. */
        onCoordsResolved?: (coords: GoogleMapsCoords | null) => void;
        /** Called when a prediction is selected and its coordinates resolve.
         * `suggestedRadiusKm` scales with the place granularity (city vs province). */
        onResolve?: (coords: {
            latitude: string;
            longitude: string;
            suggestedRadiusKm: number;
        }) => void;
    }
> = ({
    value,
    placeholder,
    searchPlaceHolder,
    onChange,
    onCoordsResolved,
    onResolve,
    countryRestrictions,
    compact,
    className,
}) => {
    const { placesService, placePredictions, getPlacePredictions, isPlacePredictionsLoading } =
        usePlacesService({
            apiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY,
            debounce: 500,
        });

    const resolveCoordinates = (selected: string) => {
        if (!onResolve || !placesService || !Array.isArray(placePredictions)) return;

        const prediction = placePredictions.find(item => item.description === selected);
        if (!prediction?.place_id) return;

        const suggestedRadiusKm = suggestedRadiusForTypes(prediction.types);

        placesService.getDetails(
            { placeId: prediction.place_id, fields: ['geometry'] },
            (
                place: {
                    geometry?: { location?: { lat: () => number; lng: () => number } };
                } | null,
                status: string,
            ) => {
                if (status !== 'OK') {
                    console.error(`Places getDetails failed for place "${selected}": ${status}`);
                    return;
                }
                const location = place?.geometry?.location;
                if (!location) return;
                onResolve({
                    latitude: String(location.lat()),
                    longitude: String(location.lng()),
                    suggestedRadiusKm,
                });
            },
        );
    };

    return (
        <div className="flex w-full flex-col">
            <ComboBox
                value={value}
                icon="location"
                compact={compact}
                placeholder={placeholder}
                searchPlaceHolder={searchPlaceHolder ?? undefined}
                options={useMemo(() => {
                    if (!placePredictions) return [];

                    if (Array.isArray(placePredictions)) {
                        return placePredictions.map(item => ({
                            value: item.description,
                            label: item.description,
                        }));
                    }

                    return [
                        {
                            value,
                            label: value,
                        },
                    ];
                }, [placePredictions])}
                isLoading={isPlacePredictionsLoading}
                onSearch={(value: string) =>
                    // Restrict suggestions to the given countries, defaulting to Belgium + the Netherlands.
                    getPlacePredictions({
                        input: value,
                        componentRestrictions: {
                            country:
                                countryRestrictions && countryRestrictions.length > 0
                                    ? countryRestrictions
                                    : ['be', 'nl'],
                        },
                    })
                }
                onChange={selected => {
                    onChange?.(selected);

                    resolveCoordinates(selected);

                    if (!onCoordsResolved) return;
                    const prediction = Array.isArray(placePredictions)
                        ? placePredictions.find(p => p.description === selected)
                        : undefined;
                    if (!prediction || !placesService) {
                        onCoordsResolved(null);
                        return;
                    }
                    placesService.getDetails(
                        { placeId: prediction.place_id, fields: ['geometry'] },
                        (
                            place: {
                                geometry?: { location?: { lat(): number; lng(): number } };
                            } | null,
                            status: string,
                        ) => {
                            if (status !== 'OK' || !place) {
                                onCoordsResolved(null);
                                return;
                            }
                            const loc = place.geometry?.location;
                            if (!loc) {
                                onCoordsResolved(null);
                                return;
                            }
                            onCoordsResolved({ lat: loc.lat(), lng: loc.lng() });
                        },
                    );
                }}
                className={cx('rounded-r-none', className)}
            />
        </div>
    );
};
